import { useMemo } from 'react';
import { AccordionRubric, Button, Stack, Text, styled } from 'shell/components';

const RubricRenderer = ({
    disabled = false,
    showGraded = true,
    showSuggestedTag = true,
    assignment,
    currentSubmission,
    form,
    setForm,
    selectedCriterionId,
    setSelectedCriterionId,
    setShowFailedDialog,
    setContentBankResourcesUsageDialog,
}) => {
    const rubric = useMemo(
        () =>
            assignment.rubric.criteria.map((criterion) => {
                const justification = currentSubmission.ai?.grading?.[criterion.id]?.justification;
                const suggestedOptionId = currentSubmission.ai?.grading?.[criterion.id]?.optionId;
                const gradingInstructions =
                    assignment.ai_grading_settings?.criteria?.[criterion.id]?.grading_instructions;
                if (!suggestedOptionId) return { ...criterion, gradingInstructions };
                return {
                    ...criterion,
                    justification,
                    options: criterion.options.map((option) => ({
                        ...option,
                        suggested: showSuggestedTag ? option.id === suggestedOptionId : false,
                    })),
                    gradingInstructions,
                };
            }),
        [assignment, currentSubmission.ai?.grading, showSuggestedTag]
    );

    const totalSuggestedScore = useMemo(
        () =>
            rubric.reduce((acc, cur) => {
                const getCriterionRate = (criterion) => {
                    return criterion.options.find(({ suggested }) => suggested)?.rate || 0;
                };
                return acc + Number(getCriterionRate(cur));
            }, 0),
        [rubric]
    );

    if (!form) return null;

    return (
        <Stack>
            <BackgroundStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                {currentSubmission.content_bank_resources_usage ? (
                    <Button variant={'borderless'} onClick={() => setContentBankResourcesUsageDialog(true)}>
                        How was the content bank used?
                    </Button>
                ) : (
                    <Text variant={'p1'} weight={'semibold'}>
                        Rubric
                    </Text>
                )}
                <Text>
                    Total Points:{' '}
                    {!Object.keys(form.criteria).length ? (
                        <Text color={'primary'} component={'span'} tooltip={{ component: 'Suggested score' }}>
                            {totalSuggestedScore}
                        </Text>
                    ) : (
                        Object.entries(form.criteria).reduce((acc, [criterionId, optionId]) => {
                            return (
                                acc +
                                Number(
                                    rubric
                                        .find(({ id }) => id === criterionId)
                                        .options.find(({ id }) => id === optionId).rate
                                )
                            );
                        }, 0)
                    )}
                    /
                    {rubric.reduce((acc, { options }) => {
                        return acc + Math.max(...options.map(({ rate }) => Number(rate)));
                    }, 0)}
                </Text>
            </BackgroundStack>
            <Stack sx={{ py: 4, px: 5 }} spacing={4}>
                {currentSubmission.is_failed && (
                    <Stack
                        sx={(theme) => ({
                            borderRadius: '8px',
                            p: theme.spacing(2, 3),
                            background: theme.palette.ensightful.warning[100],
                        })}
                        alignItems={'flex-start'}
                        spacing={2}
                    >
                        <Text color={(theme) => theme.palette.ensightful.warning[900]}>
                            We were unable to generate grades for this assignment. You can try to regenerate, or grade
                            the submission manually. You must select grades and add feedback to confirm.
                        </Text>
                        <Button variant={'borderless'} onClick={() => setShowFailedDialog(true)}>
                            Learn more
                        </Button>
                    </Stack>
                )}
                <AccordionRubric
                    showGraded={showGraded}
                    criteria={rubric}
                    disabled={disabled || currentSubmission.locked}
                    form={form.criteria}
                    selectedCriterionId={selectedCriterionId}
                    setSelectedCriterionId={setSelectedCriterionId}
                    onChange={(criterionId, optionId) => {
                        setForm((current) => {
                            return {
                                ...current,
                                criteria: {
                                    ...current.criteria,
                                    [criterionId]: optionId,
                                },
                            };
                        });
                    }}
                />
            </Stack>
        </Stack>
    );
};

const BackgroundStack = styled(Stack)(({ theme }) => {
    return {
        width: '100%',
        padding: theme.spacing(2, 5),
        background: theme.palette.ensightful.primary[50],
    };
});

export default RubricRenderer;
